import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Route, AlarmClock } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const ZONE_COLORS = {
  sourcing: { bg: '#64748b', light: 'rgba(100,116,139,0.18)', text: '#94a3b8' },
  olta: { bg: '#3b82f6', light: 'rgba(59,130,246,0.18)', text: '#93c5fd' },
  deal: { bg: '#8b5cf6', light: 'rgba(139,92,246,0.18)', text: '#c4b5fd' },
  attio: { bg: '#8b5cf6', light: 'rgba(139,92,246,0.18)', text: '#c4b5fd' },
  terminal: { bg: '#ef4444', light: 'rgba(239,68,68,0.15)', text: '#fca5a5' },
};

const EVENT_LABELS = {
  created: 'Deal created', claim: 'Claimed', add_to_agenda: 'Added to agenda',
  meeting_advance: 'Advanced in team meeting', meeting_pass: 'Passed in team meeting',
  pull_to_outreach: 'Pulled to outreach', request_approval: 'Approval requested',
  approve_letter: 'Letter approved', mark_mailed: 'Marked as mailed',
  attio_reply_webhook: 'Response logged via Attio (legacy)', log_response: 'Response logged',
  advance: 'Advanced', pass: 'Passed', timer_expired: 'SLA expired (auto-released)',
  attio_sync: 'Synced from Attio (legacy)', unrecorded: 'Stage updated',
  manual_stage_change: 'Stage changed manually',
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const fmtDuration = (days) => {
  if (days == null) return '';
  if (days < 1) {
    const h = Math.max(Math.round(days * 24), 1);
    return `${h}h`;
  }
  return `${Math.round(days * 10) / 10}d`;
};

export function DealTimeline({ projectId, stage }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    api.getDealTimeline(projectId)
      .then((res) => { if (live) setData(res.data); })
      .catch(() => {});
    return () => { live = false; };
  }, [projectId, stage]);

  if (!data || !data.segments?.length) return null;
  const { segments, timer } = data;

  return (
    <div className="bg-card border border-border rounded-sm p-4 sm:p-5 mt-6" data-testid="deal-timeline">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h3 className="font-heading text-lg text-foreground flex items-center gap-2">
          <Route className="w-4.5 h-4.5 text-blue-400" style={{ width: 18, height: 18 }} /> Deal Journey
        </h3>
        <span className="text-xs text-muted-foreground">
          {fmtDuration(data.total_days)} in pipeline · {segments.length} stage{segments.length !== 1 ? 's' : ''}
        </span>
        {timer && (
          <span
            className={`ml-auto inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm border font-medium ${timer.breached ? 'bg-red-500/10 border-red-500/30 text-red-400' : 'bg-amber-500/10 border-amber-500/30 text-amber-400'}`}
            data-testid="timeline-sla-chip">
            <AlarmClock className="w-3 h-3" />
            {timer.breached
              ? `SLA breached${timer.days_left != null && timer.days_left < 0 ? ` · ${Math.abs(Math.round(timer.days_left))}d over` : ''}`
              : `SLA · ${fmtDuration(timer.days_left)} left`}
          </span>
        )}
      </div>

      {/* Segmented journey bar */}
      <TooltipProvider delayDuration={100}>
        <div className="flex h-9 rounded-sm overflow-hidden border border-border/60" data-testid="timeline-bar">
          {segments.map((s, i) => {
            const c = ZONE_COLORS[s.zone] || ZONE_COLORS.olta;
            return (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <div
                    className="relative flex items-center justify-center min-w-0 cursor-default transition-all duration-200 hover:brightness-125"
                    style={{
                      flexGrow: Math.max(s.duration_days, 0.35),
                      flexBasis: 0,
                      minWidth: 34,
                      background: s.is_current ? c.light : c.light,
                      borderLeft: i > 0 ? '1px solid rgba(0,0,0,0.35)' : 'none',
                      boxShadow: s.is_current ? `inset 0 -3px 0 ${c.bg}` : 'none',
                    }}
                    data-testid={`timeline-segment-${i}`}
                  >
                    <span className="text-[10px] font-mono px-1 truncate" style={{ color: c.text }}>
                      {fmtDuration(s.duration_days)}
                    </span>
                    {s.is_current && (
                      <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: c.bg }} />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-card border-border rounded-sm p-3 max-w-xs">
                  <p className="text-xs font-medium text-foreground mb-1">{s.stage_label}{s.is_current ? ' (current)' : ''}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {fmtDate(s.entered_at)} → {s.exited_at ? fmtDate(s.exited_at) : 'now'} · {fmtDuration(s.duration_days)}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {EVENT_LABELS[s.entered_via] || s.entered_via || 'Entered'}{s.entered_by ? ` — ${s.entered_by}` : ''}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      {/* Step list */}
      <ol className="mt-4 space-y-1" data-testid="timeline-steps">
        {segments.map((s, i) => {
          const c = ZONE_COLORS[s.zone] || ZONE_COLORS.olta;
          return (
            <li key={i} className={`flex items-baseline gap-2.5 text-xs px-2 py-1 rounded-sm ${s.is_current ? 'bg-secondary/50' : ''}`}>
              <span className="shrink-0 w-2 h-2 rounded-full self-center" style={{ background: c.bg }} />
              <span className={`shrink-0 font-medium ${s.is_current ? 'text-foreground' : 'text-foreground/80'}`}>{s.stage_label}</span>
              <span className="text-muted-foreground truncate">
                {EVENT_LABELS[s.entered_via] || s.entered_via || ''}{s.entered_by ? ` · ${s.entered_by}` : ''}
              </span>
              <span className="ml-auto shrink-0 text-muted-foreground font-mono">
                {fmtDate(s.entered_at)} · {fmtDuration(s.duration_days)}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
